import { uuid } from "@/interfaces";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "@/lib/store.ts";
import {
  deletePropertyDetail,
  getPropertyDetail,
  fetchPropertyAppointments,
  fetchPropertyAppointmentFilterOptions,
  setAppointmentStatus,
  setAppointmentServiceParent,
  setAppointmentAssignee,
  setAppointmentSearch,
  setAppointmentPage,
  setAppointmentPageSize,
  setAppointmentSortKey,
  setAppointmentSortOrder,
} from "@/app/pmc/properties-detail/properties-detail.slice.ts";

export const usePropertyDetail = (propertyId: uuid | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { property, notFound, loading, deleting, error } = useAppSelector((state) => state.propertyDetails);

  useEffect(() => {
    if (propertyId) {
      dispatch(getPropertyDetail(propertyId));
    }
  }, [propertyId, dispatch]);

  const handleDelete = async () => {
    await dispatch(deletePropertyDetail(propertyId!));
    navigate("/properties");
  };

  return {
    property,
    notFound,
    loading,
    deleting,
    error,
    handleDelete,
  };
};

export const usePropertyAppointments = (propertyId: uuid | undefined) => {
  const dispatch = useAppDispatch();
  const { appointments } = useAppSelector((state) => state.propertyDetails);

  useEffect(() => {
    if (propertyId) {
      dispatch(fetchPropertyAppointments(propertyId));
      dispatch(fetchPropertyAppointmentFilterOptions(propertyId));
    }
  }, [
    dispatch,
    propertyId,
    appointments.filters.status,
    appointments.filters.serviceParent,
    appointments.filters.assignee,
    appointments.filters.search,
    appointments.filters.sortKey,
    appointments.filters.sortOrder,
    appointments.pagination.page,
    appointments.pagination.pageSize,
  ]);

  const updateStatus = (status: string) => {
    dispatch(setAppointmentStatus(status as any));
  };

  const updateServiceParent = (serviceParent: string) => {
    dispatch(setAppointmentServiceParent(serviceParent));
  };

  const updateAssignee = (assignee: string) => {
    dispatch(setAppointmentAssignee(assignee));
  };

  const updateSearch = (search: string) => {
    dispatch(setAppointmentSearch(search));
  };

  const updatePage = (page: number) => {
    dispatch(setAppointmentPage(page));
  };

  const updatePageSize = (pageSize: number) => {
    dispatch(setAppointmentPageSize(pageSize));
  };

  const updateSortKey = (sortKey: string) => {
    dispatch(setAppointmentSortKey(sortKey));
  };

  const updateSortOrder = (sortOrder: "asc" | "desc") => {
    dispatch(setAppointmentSortOrder(sortOrder));
  };

  return {
    appointments: appointments.list,
    loading: appointments.loading,
    error: appointments.error,
    total: appointments.total,
    filters: appointments.filters,
    pagination: appointments.pagination,
    filterOptions: appointments.filterOptions,
    updateStatus,
    updateServiceParent,
    updateAssignee,
    updateSearch,
    updatePage,
    updatePageSize,
    updateSortKey,
    updateSortOrder,
  };
};
